/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';

import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import dashboardProtos from 'utils/protos';

const { BusinessPacingLimit, BusinessPacingDayLimit, BusinessPacingWeekday } = dashboardProtos;
/**
 * Fetch the business pacing.
 *
 * @returns {Object} A redux TockApi action.
 */
export function fetchPacing(): ApiAction {
  return {
    TockApi: {
      getProto: '/business/pacing',
      type: AppConstants.BUSINESS_PACING_FETCH,
      onError: _.noop,
    },
  };
}

/**
 * Fetch the business weekday pacing.
 *
 * @returns {Object} A redux TockApi action.
 */
export function fetchWeekdayPacing(): ApiAction {
  return {
    TockApi: {
      getListProto: '/business/pacing/weekday',
      type: AppConstants.BUSINESS_PACING_WEEKDAY_FETCH,
      onError: _.noop,
    },
  };
}

/**
 * Update the given business pacing.
 *
 * @returns {Object} A redux TockApi action.
 */
export function updatePacing(pacing: dashboard.BusinessPacingLimit): ApiAction {
  return {
    TockApi: {
      postProto: '/business/pacing',
      type: AppConstants.NULL,
      request: pacing,
    },
  };
}

/**
 * Fetch the business pacing for the day.
 *
 * @returns {Object} A redux TockApi action.
 */
export function fetchDayPacing(
  date: string,
  serviceLevelCapacitySettingsEnabled: boolean
): ApiAction {
  return {
    TockApi: {
      getListProto: `/business/pacing/day/all?date=${date}`,
      type: AppConstants.BUSINESS_DAY_PACING_FETCH,
      onError: _.noop,
      context: {
        // This is a workaround for the issue described here: https://github.com/tocktix/admin/pull/17506#discussion_r766259080
        // It can be removed once TACO_CAPACITY_SETTINGS is enabled for all businesses
        serviceLevelCapacitySettingsEnabled,
      },
    },
  };
}

/**
 * Saves all pacing entities.
 * @param {Object} pacing The pacing object.
 * @param {Array} dayPacingInsert The day pacing to be inserted
 * @param {Array} dayPacingUpdate The day pacing to be inserted
 * @param {Array} dayPacingDelete The day pacing to be inserted
 * @returns {Object} A redux TockApi action.
 */
export function updateDayPacingObjects(
  dayPacingInsert: dashboard.IBusinessPacingDayLimit[],
  dayPacingUpdate: dashboard.BusinessPacingDayLimit[],
  dayPacingDelete: dashboard.BusinessPacingDayLimit[]
): ApiAction {
  const requests: any[] = [];
  dayPacingInsert.forEach((pacingLimit) => {
    requests.push({
      path: '/business/pacing/day',
      method: 'POST',
      payload: BusinessPacingDayLimit.fromObject(pacingLimit),
      requestType: BusinessPacingDayLimit,
    });
  });
  dayPacingUpdate.forEach((pacingLimit) => {
    requests.push({
      path: '/business/pacing/day',
      method: 'PUT',
      payload: BusinessPacingDayLimit.fromObject(pacingLimit),
      requestType: BusinessPacingDayLimit,
    });
  });
  dayPacingDelete.forEach((pacingLimit) => {
    requests.push({
      path: '/business/pacing/day/' + pacingLimit.id,
      method: 'DELETE',
    });
  });
  return {
    TockApi: {
      batchProto: requests,
      type: AppConstants.NULL,
    },
  };
}

/**
 * Saves all pacing entities.
 * @param {Object} pacing The pacing object.
 * @param {Array} weekdayPacingInsert The weekday pacing to be inserted
 * @param {Array} weekdayPacingUpdate The weekday pacing to be inserted
 * @param {Array} weekdayPacingDelete The weekday pacing to be inserted
 * @returns {Object} A redux TockApi action.
 */
export function updatePacingObjects(
  pacing: dashboard.BusinessPacingLimit,
  weekdayPacingInsert: dashboard.IBusinessPacingWeekday[],
  weekdayPacingUpdate: dashboard.BusinessPacingWeekday[],
  weekdayPacingDelete: dashboard.BusinessPacingWeekday[]
): ApiAction {
  const requests: any[] = [];
  requests.push({
    path: '/business/pacing',
    method: 'POST',
    payload: pacing,
    requestType: BusinessPacingLimit,
  });
  weekdayPacingInsert.forEach((pacingLimit) => {
    requests.push({
      path: '/business/pacing/weekday',
      method: 'POST',
      payload: BusinessPacingWeekday.fromObject(pacingLimit),
      requestType: BusinessPacingWeekday,
    });
  });
  weekdayPacingUpdate.forEach((pacingLimit) => {
    requests.push({
      path: '/business/pacing/weekday',
      method: 'PUT',
      payload: BusinessPacingWeekday.fromObject(pacingLimit),
      requestType: BusinessPacingWeekday,
    });
  });
  weekdayPacingDelete.forEach((pacingLimit) => {
    requests.push({
      path: '/business/pacing/weekday/' + pacingLimit.id,
      method: 'DELETE',
    });
  });
  return {
    TockApi: {
      batchProto: requests,
      type: AppConstants.NULL,
    },
  };
}
